import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Chip } from 'primereact/chip';
import { Panel } from 'primereact/panel';
import { WorkflowExecution, ResearchProject } from '../types';
import { apiService } from '../services/api';

interface WorkflowReviewPanelProps {
  workflow: WorkflowExecution | null;
  projectId?: string;
  project?: ResearchProject; // Add project prop to get data directly from DynamoDB
  onDeployToKobo?: (questionnaire: any) => Promise<void>;
  onRefresh?: () => void;
}

interface IndicatorData {
  indicator_id: string;
  name: string;
  definition: string;
  measurement_type: 'numerical' | 'categorical' | 'binary';
  data_source: string;
}

interface QuestionnaireData {
  questionnaire_id: string;
  title: string;
  description: string;
  language: string;
  sections: Array<{
    section_id: string;
    title: string;
    description: string;
    questions: Array<{
      question_id: string;
      text: string;
      type: string;
      required: boolean;
      choices?: string[];
    }>;
  }>;
}

interface KoboDeploymentData {
  kobo_form_id: string;
  kobo_form_url: string;
  deployment_url: string;
  deployment_status: string;
}

const WorkflowReviewPanel: React.FC<WorkflowReviewPanelProps> = ({
  workflow,
  projectId,
  project,
  onDeployToKobo,
  onRefresh
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [showQuestionnairePreview, setShowQuestionnairePreview] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workflowData, setWorkflowData] = useState<WorkflowExecution | null>(null); // Use state instead of ref
  const fetchedRef = useRef<string | null>(null);
  const toast = useRef<Toast>(null);

  // Debug logging to see what workflow data we're receiving
  console.log('WorkflowReviewPanel - workflow prop:', workflow);
  console.log('WorkflowReviewPanel - workflow output:', workflow?.output);
  console.log('WorkflowReviewPanel - workflow output type:', typeof workflow?.output);
  console.log('WorkflowReviewPanel - workflowData state:', workflowData);

  // Fetch the latest completed workflow if no current workflow is provided
  useEffect(() => {
    const fetchLatestWorkflow = async () => {
      // Only fetch if panel is shown and no workflow is provided
      if (workflow || !projectId) return;
      
      // Reset fetch ref when showReviewPanel changes to allow re-fetching
      if (fetchedRef.current !== projectId) {
        fetchedRef.current = projectId;
      }
      
      setLoading(true);
      
      try {
        console.log('Fetching workflow history for project:', projectId);
        const response = await apiService.listProjectWorkflows(projectId);
        
        if (response.success && response.data && response.data.workflows.length > 0) {
          // Get the most recent workflow
          const mostRecent = response.data.workflows[0];
          console.log('Most recent workflow from history:', mostRecent);
          
          // Fetch detailed workflow status
          const detailResponse = await apiService.getWorkflowStatus(projectId, mostRecent.execution_arn);
          console.log('Detailed workflow status:', detailResponse);
          console.log('detailResponse.data:', detailResponse.data);
          console.log('detailResponse keys:', Object.keys(detailResponse));
          
          // Handle both wrapped and direct response formats
          let workflowOutput;
          let workflowStatus;
          let workflowStartedAt;
          let workflowEndedAt;
          let workflowCurrentStep;
          let workflowError;
          let workflowCause;
          
          if (detailResponse.data) {
            // Wrapped in ApiResponse format
            workflowOutput = detailResponse.data.output;
            workflowStatus = detailResponse.data.status;
            workflowStartedAt = detailResponse.data.started_at;
            workflowEndedAt = detailResponse.data.ended_at;
            workflowCurrentStep = detailResponse.data.current_step;
            workflowError = detailResponse.data.error;
            workflowCause = detailResponse.data.cause;
          } else if ((detailResponse as any).output) {
            // Direct response format - cast to any to access properties
            const directResponse = detailResponse as any;
            workflowOutput = directResponse.output;
            workflowStatus = directResponse.status;
            workflowStartedAt = directResponse.started_at;
            workflowEndedAt = directResponse.ended_at;
            workflowCurrentStep = directResponse.current_step;
            workflowError = directResponse.error;
            workflowCause = directResponse.cause;
          }
          
          console.log('Extracted workflow output:', workflowOutput);
          console.log('Extracted workflow status:', workflowStatus);
          
          if (detailResponse.success && workflowOutput) {
            const workflowDataObj: WorkflowExecution = {
              execution_arn: mostRecent.execution_arn,
              execution_name: mostRecent.execution_name || '',
              workflow_id: mostRecent.workflow_id || '',
              status: workflowStatus as WorkflowExecution['status'],
              started_at: workflowStartedAt || mostRecent.started_at,
              ended_at: workflowEndedAt || mostRecent.ended_at,
              current_step: workflowCurrentStep,
              output: workflowOutput,
              error: workflowError,
              cause: workflowCause
            };
            
            console.log('Setting workflow data in state:', workflowDataObj);
            console.log('Workflow output data:', workflowDataObj.output);
            console.log('Workflow output type:', typeof workflowDataObj.output);
            
            // Set in state to trigger re-render
            setWorkflowData(workflowDataObj);
            console.log('State update called');
          } else {
            console.log('Failed to get detailed workflow status:', detailResponse);
          }
        } else {
          console.log('No workflows found for project:', projectId);
        }
      } catch (error) {
        console.error('Error fetching latest workflow:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestWorkflow();
  }, [workflow, projectId]); // Removed fetchedRef dependency to allow re-fetching

  // Use the current workflow if available, otherwise use the state data
  const activeWorkflow = workflow || workflowData;
  console.log('Active workflow for display:', activeWorkflow);
  console.log('workflowData state:', workflowData);
  console.log('workflow prop:', workflow);

  // Extract real indicators from workflow output or project data
  const getIndicatorsFromWorkflow = (): IndicatorData[] => {
    console.log('getIndicatorsFromWorkflow called');
    console.log('activeWorkflow:', activeWorkflow);
    console.log('project:', project);
    
    // First, try to get from project data directly (most reliable for completed workflows)
    if (project?.indicators && Array.isArray(project.indicators)) {
      console.log('Found indicators in project data:', project.indicators);
      return project.indicators.map((indicator: any) => ({
        indicator_id: indicator.indicator_id || indicator.indicatorId || Math.random().toString(),
        name: indicator.name || 'Unnamed Indicator',
        definition: indicator.definition || indicator.description || 'No definition available',
        measurement_type: indicator.measurement_type || indicator.measurementType || indicator.type || 'unknown',
        data_source: indicator.data_source || indicator.dataSource || 'Unknown source'
      }));
    }
    
    // Fall back to workflow output
    if (!activeWorkflow?.output) {
      console.log('No activeWorkflow.output and no project indicators, returning empty array');
      return [];
    }
    
    try {
      const output = typeof activeWorkflow.output === 'string' ? JSON.parse(activeWorkflow.output) : activeWorkflow.output;
      console.log('Parsed workflow output:', output);
      console.log('Output keys:', Object.keys(output || {}));
      
      // Try Step Functions format first (with .Payload wrapper)
      const indicatorResult = output.indicator_result?.Payload;
      console.log('indicator_result:', output.indicator_result);
      console.log('indicator_result.Payload:', indicatorResult);
      
      if (indicatorResult?.indicators && Array.isArray(indicatorResult.indicators)) {
        console.log('Found indicators array in Step Functions format:', indicatorResult.indicators);
        return indicatorResult.indicators.map((indicator: any) => ({
          indicator_id: indicator.indicator_id || indicator.id || Math.random().toString(),
          name: indicator.name || 'Unnamed Indicator',
          definition: indicator.definition || indicator.description || 'No definition available',
          measurement_type: indicator.measurement_type || indicator.type || 'unknown',
          data_source: indicator.data_source || 'Unknown source'
        }));
      }
      
      // Try direct format (without .Payload wrapper) - for resume workflow
      if (output.indicator_result?.indicators && Array.isArray(output.indicator_result.indicators)) {
        console.log('Found indicators array in direct format:', output.indicator_result.indicators);
        return output.indicator_result.indicators.map((indicator: any) => ({
          indicator_id: indicator.indicator_id || indicator.id || Math.random().toString(),
          name: indicator.name || 'Unnamed Indicator',
          definition: indicator.definition || indicator.description || 'No definition available',
          measurement_type: indicator.measurement_type || indicator.type || 'unknown',
          data_source: indicator.data_source || 'Unknown source'
        }));
      }
      
      // Check if indicators are at the root level
      if (output.indicators && Array.isArray(output.indicators)) {
        console.log('Found indicators at root level:', output.indicators);
        return output.indicators.map((indicator: any) => ({
          indicator_id: indicator.indicator_id || indicator.id || Math.random().toString(),
          name: indicator.name || 'Unnamed Indicator',
          definition: indicator.definition || indicator.description || 'No definition available',
          measurement_type: indicator.measurement_type || indicator.type || 'unknown',
          data_source: indicator.data_source || 'Unknown source'
        }));
      }
      
      // Check other possible locations
      console.log('Checking all possible indicator locations in output...');
      console.log('Full output structure:', JSON.stringify(output, null, 2));
    } catch (error) {
      console.error('Error parsing indicators from workflow output:', error);
    }
    
    console.log('Returning empty indicators array');
    return [];
  };

  const indicators = getIndicatorsFromWorkflow();
  console.log('Final indicators array:', indicators);
  console.log('Indicators count:', indicators.length);

  // Extract real questionnaire from workflow output or project data
  const getQuestionnaireFromWorkflow = (): QuestionnaireData => {
    // First, try to get from project data directly (most reliable for completed workflows)
    if (project?.questionnaire) {
      console.log('Found questionnaire in project data:', project.questionnaire);
      const q = project.questionnaire;
      return {
        questionnaire_id: q.questionnaireId || 'from_project',
        title: q.title || 'Generated Questionnaire',
        description: q.metadata?.description || 'Auto-generated from indicators',
        language: q.metadata?.language || 'en',
        sections: q.sections.map(section => ({
          section_id: section.sectionId,
          title: section.title,
          description: '',
          questions: section.questions.map(question => ({
            question_id: question.questionId,
            text: question.text,
            type: question.type,
            required: true,
            choices: question.choices
          }))
        }))
      };
    }
    
    // Fall back to workflow output
    if (!activeWorkflow?.output) {
      return {
        questionnaire_id: 'default',
        title: 'No questionnaire available',
        description: 'Questionnaire data not found in workflow output or project data',
        language: 'en',
        sections: []
      };
    }
    
    try {
      const output = typeof activeWorkflow.output === 'string' ? JSON.parse(activeWorkflow.output) : activeWorkflow.output;
      
      // Try Step Functions format first (with .Payload wrapper)
      const questionnaireResult = output.questionnaire_result?.Payload;
      
      if (questionnaireResult?.questionnaire) {
        const q = questionnaireResult.questionnaire;
        return {
          questionnaire_id: q.questionnaire_id || 'generated',
          title: q.title || output.questionnaire_config?.title || 'Generated Questionnaire',
          description: q.description || output.questionnaire_config?.description || 'Auto-generated from indicators',
          language: q.language || output.questionnaire_config?.language || 'en',
          sections: q.sections || []
        };
      }
      
      // Try direct format (without .Payload wrapper) - for resume workflow
      if (output.questionnaire_result?.questionnaire) {
        const q = output.questionnaire_result.questionnaire;
        return {
          questionnaire_id: q.questionnaire_id || 'generated',
          title: q.title || output.questionnaire_config?.title || 'Generated Questionnaire',
          description: q.description || output.questionnaire_config?.description || 'Auto-generated from indicators',
          language: q.language || output.questionnaire_config?.language || 'en',
          sections: q.sections || []
        };
      }
      
      // Check if questionnaire is at root level
      if (output.questionnaire) {
        const q = output.questionnaire;
        return {
          questionnaire_id: q.questionnaire_id || 'generated',
          title: q.title || 'Generated Questionnaire',
          description: q.description || 'Auto-generated from indicators',
          language: q.language || 'en',
          sections: q.sections || []
        };
      }
      
      // Fallback: create basic questionnaire from indicators if questionnaire not found
      const indicatorResult = output.indicator_result?.Payload || output.indicator_result;
      if (indicatorResult?.indicators) {
        const questions = indicatorResult.indicators.map((indicator: any, index: number) => ({
          question_id: `q_${index + 1}`,
          text: `What is your ${indicator.name?.toLowerCase()}?`,
          type: indicator.measurement_type === 'numerical' ? 'integer' : 
                indicator.measurement_type === 'binary' ? 'select_one' : 'select_one',
          required: true,
          choices: indicator.measurement_type === 'binary' ? ['Yes', 'No'] : 
                  indicator.measurement_type === 'categorical' ? ['Option 1', 'Option 2', 'Option 3'] : undefined
        }));
        
        return {
          questionnaire_id: 'generated_from_indicators',
          title: output.questionnaire_config?.title || 'Generated Survey',
          description: output.questionnaire_config?.description || 'Survey generated from extracted indicators',
          language: 'en',
          sections: [{
            section_id: 'main_section',
            title: 'Main Questions',
            description: 'Questions generated from research indicators',
            questions
          }]
        };
      }
    } catch (error) {
      console.error('Error parsing questionnaire from workflow output:', error);
    }
    
    return {
      questionnaire_id: 'error',
      title: 'Error loading questionnaire',
      description: 'Failed to parse questionnaire data',
      language: 'en',
      sections: []
    };
  };

  const questionnaire = getQuestionnaireFromWorkflow();

  // Extract real KoboToolbox deployment data from workflow output or project data
  const getKoboDeploymentFromWorkflow = (): KoboDeploymentData => {
    // First, try to get from project data directly (most reliable)
    if (project?.koboFormId) {
      console.log('Found Kobo deployment in project data');
      return {
        kobo_form_id: project.koboFormId,
        kobo_form_url: project.koboFormUrl || `https://kf.kobotoolbox.org/#/forms/${project.koboFormId}`,
        deployment_url: project.koboFormUrl || `https://ee.kobotoolbox.org/x/${project.koboFormId}`,
        deployment_status: 'deployed'
      };
    }
    
    // Fall back to workflow output
    if (!activeWorkflow?.output) {
      return {
        kobo_form_id: '',
        kobo_form_url: '',
        deployment_url: '',
        deployment_status: 'not_deployed'
      };
    }
    
    try {
      const output = typeof activeWorkflow.output === 'string' ? JSON.parse(activeWorkflow.output) : activeWorkflow.output;
      
      // Try Step Functions format first (with .Payload wrapper)
      const koboResult = output.kobo_deployment_result?.Payload;
      
      if (koboResult?.kobo_form_id) {
        return {
          kobo_form_id: koboResult.kobo_form_id,
          kobo_form_url: koboResult.kobo_form_url || `https://kf.kobotoolbox.org/#/forms/${koboResult.kobo_form_id}`,
          deployment_url: koboResult.deployment_url || koboResult.survey_url || `https://ee.kobotoolbox.org/x/${koboResult.kobo_form_id}`,
          deployment_status: koboResult.deployment_status || 'deployed'
        };
      }
      
      // Try direct format (without .Payload wrapper) - for resume workflow
      if (output.kobo_deployment_result?.kobo_form_id) {
        const koboResult = output.kobo_deployment_result;
        return {
          kobo_form_id: koboResult.kobo_form_id,
          kobo_form_url: koboResult.kobo_form_url || `https://kf.kobotoolbox.org/#/forms/${koboResult.kobo_form_id}`,
          deployment_url: koboResult.deployment_url || koboResult.survey_url || `https://ee.kobotoolbox.org/x/${koboResult.kobo_form_id}`,
          deployment_status: koboResult.deployment_status || 'deployed'
        };
      }
      
      // Check if there's deployment info in the main output
      if (output.kobo_form_id) {
        return {
          kobo_form_id: output.kobo_form_id,
          kobo_form_url: output.kobo_form_url || `https://kf.kobotoolbox.org/#/forms/${output.kobo_form_id}`,
          deployment_url: output.deployment_url || `https://ee.kobotoolbox.org/x/${output.kobo_form_id}`,
          deployment_status: 'deployed'
        };
      }
    } catch (error) {
      console.error('Error parsing KoboToolbox deployment from workflow output:', error);
    }
    
    return {
      kobo_form_id: '',
      kobo_form_url: '',
      deployment_url: '',
      deployment_status: 'not_deployed'
    };
  };

  const koboDeployment = getKoboDeploymentFromWorkflow();

  const getStepStatus = (stepName: string) => {
    if (!activeWorkflow) return 'pending';
    
    const stepOrder = ['ExtractIndicators', 'DesignQuestionnaire', 'DeployToKobo'];
    const currentStepIndex = stepOrder.indexOf(activeWorkflow.current_step || '');
    const thisStepIndex = stepOrder.indexOf(stepName);
    
    if (activeWorkflow.status === 'SUCCEEDED') return 'completed';
    if (activeWorkflow.status === 'FAILED' && thisStepIndex <= currentStepIndex) return 'failed';
    if (thisStepIndex < currentStepIndex) return 'completed';
    if (thisStepIndex === currentStepIndex && activeWorkflow.status === 'RUNNING') return 'running';
    return 'pending';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return 'pi pi-check-circle';
      case 'running': return 'pi pi-spin pi-spinner';
      case 'failed': return 'pi pi-times-circle';
      default: return 'pi pi-clock';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#22c55e';
      case 'running': return '#3b82f6';
      case 'failed': return '#ef4444';
      default: return '#9ca3af';
    }
  };

  const handleDeployToKobo = () => {
    confirmDialog({
      message: 'Are you sure you want to deploy this questionnaire to KoboToolbox? This will create a new form in your KoboToolbox account.',
      header: 'Confirm Deployment',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        if (!onDeployToKobo) return;
        
        setDeploying(true);
        try {
          await onDeployToKobo(questionnaire);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Questionnaire deployed to KoboToolbox successfully!'
          });
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to deploy questionnaire to KoboToolbox'
          });
        } finally {
          setDeploying(false);
        }
      }
    });
  };

  const renderIndicatorsStep = () => {
    const status = getStepStatus('ExtractIndicators');
    
    return (
      <Card className="mb-3">
        <div className="flex align-items-center justify-content-between mb-3">
          <div className="flex align-items-center gap-2">
            <i 
              className={getStatusIcon(status)} 
              style={{ color: getStatusColor(status), fontSize: '1.25rem' }}
            />
            <h3 className="m-0">Step 1: Indicator Extraction</h3>
            <Tag 
              value={status.charAt(0).toUpperCase() + status.slice(1)} 
              severity={
                status === 'completed' ? 'success' :
                status === 'running' ? 'info' :
                status === 'failed' ? 'danger' : 'secondary'
              }
            />
          </div>
          {onRefresh && (
            <Button
              icon="pi pi-refresh"
              text
              size="small"
              onClick={onRefresh}
              tooltip="Refresh"
            />
          )}
        </div>

        {status === 'pending' && (
          <div className="text-center py-4">
            <i className="pi pi-clock text-4xl text-400 mb-3" />
            <p className="text-600">Waiting for indicator extraction to begin...</p>
          </div>
        )}

        {status === 'running' && (
          <div className="text-center py-4">
            <i className="pi pi-spin pi-spinner text-4xl text-primary mb-3" />
            <p className="text-600">Analyzing TOR document and extracting indicators...</p>
          </div>
        )}

        {(status === 'completed' || status === 'failed') && (
          <div>
            <div className="mb-3">
              <h4 className="text-lg font-semibold mb-2">Extracted Indicators ({indicators.length})</h4>
              <p className="text-600 text-sm mb-3">
                These indicators were automatically extracted from your TOR document and will be used to generate survey questions.
              </p>
            </div>

            <DataTable 
              value={indicators} 
              size="small"
              className="p-datatable-sm"
            >
              <Column 
                field="name" 
                header="Indicator Name"
                style={{ width: '25%' }}
                body={(rowData) => (
                  <div className="font-medium">{rowData.name}</div>
                )}
              />
              <Column 
                field="definition" 
                header="Definition"
                style={{ width: '40%' }}
                body={(rowData) => (
                  <div className="text-sm text-600">{rowData.definition}</div>
                )}
              />
              <Column 
                field="measurement_type" 
                header="Type"
                style={{ width: '15%' }}
                body={(rowData) => (
                  <Chip 
                    label={rowData.measurement_type} 
                    className="text-xs"
                    style={{
                      backgroundColor: 
                        rowData.measurement_type === 'numerical' ? '#dbeafe' :
                        rowData.measurement_type === 'categorical' ? '#fef3c7' : '#dcfce7',
                      color:
                        rowData.measurement_type === 'numerical' ? '#1e40af' :
                        rowData.measurement_type === 'categorical' ? '#92400e' : '#166534'
                    }}
                  />
                )}
              />
              <Column 
                field="data_source" 
                header="Data Source"
                style={{ width: '20%' }}
                body={(rowData) => (
                  <div className="text-sm">{rowData.data_source}</div>
                )}
              />
            </DataTable>

            <div className="mt-3 p-3 bg-blue-50 border-round">
              <div className="flex align-items-center gap-2 mb-2">
                <i className="pi pi-info-circle text-blue-600" />
                <span className="font-medium text-blue-900">Extraction Summary</span>
              </div>
              <div className="text-sm text-blue-800">
                <p className="m-0 mb-1">• {indicators.length} indicators successfully extracted</p>
                <p className="m-0 mb-1">• {indicators.filter(i => i.measurement_type === 'numerical').length} numerical indicators</p>
                <p className="m-0 mb-1">• {indicators.filter(i => i.measurement_type === 'categorical').length} categorical indicators</p>
                <p className="m-0">• {indicators.filter(i => i.measurement_type === 'binary').length} binary indicators</p>
              </div>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderQuestionnaireStep = () => {
    const status = getStepStatus('DesignQuestionnaire');
    const indicatorStatus = getStepStatus('ExtractIndicators');
    
    return (
      <Card className="mb-3">
        <div className="flex align-items-center justify-content-between mb-3">
          <div className="flex align-items-center gap-2">
            <i 
              className={getStatusIcon(status)} 
              style={{ color: getStatusColor(status), fontSize: '1.25rem' }}
            />
            <h3 className="m-0">Step 2: Questionnaire Design</h3>
            <Tag 
              value={status.charAt(0).toUpperCase() + status.slice(1)} 
              severity={
                status === 'completed' ? 'success' :
                status === 'running' ? 'info' :
                status === 'failed' ? 'danger' : 'secondary'
              }
            />
          </div>
          {status === 'completed' && (
            <Button
              label="Preview Questionnaire"
              icon="pi pi-eye"
              size="small"
              outlined
              onClick={() => setShowQuestionnairePreview(true)}
            />
          )}
        </div>

        {status === 'pending' && indicatorStatus !== 'completed' && (
          <div className="text-center py-4">
            <i className="pi pi-lock text-4xl text-400 mb-3" />
            <p className="text-600">Waiting for indicator extraction to complete...</p>
          </div>
        )}

        {status === 'pending' && indicatorStatus === 'completed' && (
          <div className="text-center py-4">
            <i className="pi pi-clock text-4xl text-400 mb-3" />
            <p className="text-600">Ready to design questionnaire from extracted indicators...</p>
          </div>
        )}

        {status === 'running' && (
          <div className="text-center py-4">
            <i className="pi pi-spin pi-spinner text-4xl text-primary mb-3" />
            <p className="text-600">Converting indicators into structured survey questions...</p>
          </div>
        )}

        {(status === 'completed' || status === 'failed') && (
          <div>
            <div className="mb-3">
              <h4 className="text-lg font-semibold mb-2">Generated Questionnaire</h4>
              <p className="text-600 text-sm mb-3">
                Survey questions have been automatically generated from the extracted indicators.
              </p>
            </div>

            <div className="grid">
              <div className="col-12 md:col-6">
                <Panel header="Questionnaire Details" className="mb-3">
                  <div className="flex flex-column gap-2">
                    <div>
                      <label className="font-medium text-sm text-600">Title:</label>
                      <p className="m-0 mt-1">{questionnaire.title}</p>
                    </div>
                    <div>
                      <label className="font-medium text-sm text-600">Description:</label>
                      <p className="m-0 mt-1 text-sm">{questionnaire.description}</p>
                    </div>
                    <div>
                      <label className="font-medium text-sm text-600">Language:</label>
                      <p className="m-0 mt-1">{questionnaire.language.toUpperCase()}</p>
                    </div>
                  </div>
                </Panel>
              </div>
              
              <div className="col-12 md:col-6">
                <Panel header="Question Summary" className="mb-3">
                  <div className="flex flex-column gap-2">
                    <div className="flex justify-content-between">
                      <span className="text-600">Total Questions:</span>
                      <span className="font-medium">{questionnaire.sections[0]?.questions?.length || 0}</span>
                    </div>
                    <div className="flex justify-content-between">
                      <span className="text-600">Required Questions:</span>
                      <span className="font-medium">
                        {questionnaire.sections[0]?.questions?.filter(q => q.required).length || 0}
                      </span>
                    </div>
                    <div className="flex justify-content-between">
                      <span className="text-600">Question Types:</span>
                      <span className="font-medium">
                        {new Set(questionnaire.sections[0]?.questions?.map(q => q.type) || []).size}
                      </span>
                    </div>
                  </div>
                </Panel>
              </div>
            </div>

            <Panel header="Questions Preview" className="mb-3">
              <div className="flex flex-column gap-3">
                {questionnaire.sections[0]?.questions?.slice(0, 3).map((question, index) => (
                  <div key={question.question_id} className="border-bottom-1 surface-border pb-3">
                    <div className="flex align-items-start gap-2 mb-2">
                      <span className="bg-primary text-white border-circle w-2rem h-2rem flex align-items-center justify-content-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="m-0 font-medium">{question.text}</p>
                        <div className="flex align-items-center gap-2 mt-2">
                          <Chip 
                            label={question.type} 
                            className="text-xs"
                            style={{ backgroundColor: '#f3f4f6', color: '#374151' }}
                          />
                          {question.required && (
                            <Chip 
                              label="Required" 
                              className="text-xs"
                              style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}
                            />
                          )}
                        </div>
                        {question.choices && (
                          <div className="mt-2">
                            <span className="text-sm text-600">Choices: </span>
                            <span className="text-sm">{question.choices.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(questionnaire.sections[0]?.questions?.length || 0) > 3 && (
                  <div className="text-center">
                    <Button
                      label={`View All ${questionnaire.sections[0]?.questions?.length || 0} Questions`}
                      icon="pi pi-eye"
                      text
                      size="small"
                      onClick={() => setShowQuestionnairePreview(true)}
                    />
                  </div>
                )}
              </div>
            </Panel>

            <div className="mt-3 p-3 bg-green-50 border-round">
              <div className="flex align-items-center gap-2 mb-2">
                <i className="pi pi-check-circle text-green-600" />
                <span className="font-medium text-green-900">Design Summary</span>
              </div>
              <div className="text-sm text-green-800">
                <p className="m-0 mb-1">• Questionnaire successfully generated from {indicators.length} indicators</p>
                <p className="m-0 mb-1">• {questionnaire.sections[0]?.questions?.length || 0} questions created across {questionnaire.sections.length} section(s)</p>
                <p className="m-0">• Ready for review and deployment to KoboToolbox</p>
              </div>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderKoboDeploymentStep = () => {
    const status = getStepStatus('DeployToKobo');
    const questionnaireStatus = getStepStatus('DesignQuestionnaire');
    
    return (
      <Card className="mb-3">
        <div className="flex align-items-center justify-content-between mb-3">
          <div className="flex align-items-center gap-2">
            <i 
              className={getStatusIcon(status)} 
              style={{ color: getStatusColor(status), fontSize: '1.25rem' }}
            />
            <h3 className="m-0">Step 3: KoboToolbox Deployment</h3>
            <Tag 
              value={status === 'pending' && questionnaireStatus === 'completed' ? 'Ready' : status.charAt(0).toUpperCase() + status.slice(1)} 
              severity={
                status === 'completed' ? 'success' :
                status === 'running' ? 'info' :
                status === 'failed' ? 'danger' :
                questionnaireStatus === 'completed' ? 'warning' : 'secondary'
              }
            />
          </div>
          {questionnaireStatus === 'completed' && status === 'pending' && onDeployToKobo && (
            <Button
              label="Deploy to KoboToolbox"
              icon="pi pi-send"
              size="small"
              loading={deploying}
              onClick={handleDeployToKobo}
            />
          )}
        </div>

        {status === 'pending' && questionnaireStatus !== 'completed' && (
          <div className="text-center py-4">
            <i className="pi pi-lock text-4xl text-400 mb-3" />
            <p className="text-600">Waiting for questionnaire design to complete...</p>
          </div>
        )}

        {status === 'pending' && questionnaireStatus === 'completed' && (
          <div className="text-center py-4">
            <i className="pi pi-send text-4xl text-orange-500 mb-3" />
            <p className="text-600 mb-3">Questionnaire is ready for deployment to KoboToolbox</p>
            <div className="bg-orange-50 border-round p-3 text-left">
              <div className="flex align-items-center gap-2 mb-2">
                <i className="pi pi-info-circle text-orange-600" />
                <span className="font-medium text-orange-900">Manual Approval Required</span>
              </div>
              <p className="text-sm text-orange-800 m-0">
                Review the questionnaire above and click "Deploy to KoboToolbox" when you're ready to create the form in your KoboToolbox account.
              </p>
            </div>
          </div>
        )}

        {status === 'running' && (
          <div className="text-center py-4">
            <i className="pi pi-spin pi-spinner text-4xl text-primary mb-3" />
            <p className="text-600">Converting questionnaire to XLSForm and deploying to KoboToolbox...</p>
          </div>
        )}

        {status === 'completed' && (
          <div>
            <div className="mb-3">
              <h4 className="text-lg font-semibold mb-2">Deployment Successful</h4>
              <p className="text-600 text-sm mb-3">
                Your questionnaire has been successfully deployed to KoboToolbox and is ready for data collection.
              </p>
            </div>

            <div className="grid">
              <div className="col-12 md:col-6">
                <Panel header="KoboToolbox Form Details" className="mb-3">
                  <div className="flex flex-column gap-3">
                    <div>
                      <label className="font-medium text-sm text-600">Form ID:</label>
                      <p className="m-0 mt-1 font-mono text-sm">{koboDeployment.kobo_form_id}</p>
                    </div>
                    <div>
                      <label className="font-medium text-sm text-600">Status:</label>
                      <div className="mt-1">
                        <Tag value="Deployed" severity="success" />
                      </div>
                    </div>
                  </div>
                </Panel>
              </div>
              
              <div className="col-12 md:col-6">
                <Panel header="Quick Actions" className="mb-3">
                  <div className="flex flex-column gap-2">
                    <Button
                      label="Open in KoboToolbox"
                      icon="pi pi-external-link"
                      size="small"
                      outlined
                      onClick={() => window.open(koboDeployment.kobo_form_url, '_blank')}
                    />
                    <Button
                      label="Copy Survey Link"
                      icon="pi pi-copy"
                      size="small"
                      outlined
                      onClick={() => {
                        navigator.clipboard.writeText(koboDeployment.deployment_url);
                        toast.current?.show({
                          severity: 'success',
                          summary: 'Copied',
                          detail: 'Survey link copied to clipboard'
                        });
                      }}
                    />
                  </div>
                </Panel>
              </div>
            </div>

            <Panel header="Deployment URLs" className="mb-3">
              <div className="flex flex-column gap-3">
                <div>
                  <label className="font-medium text-sm text-600">Form Management URL:</label>
                  <div className="flex align-items-center gap-2 mt-1">
                    <code className="text-sm bg-gray-100 p-2 border-round flex-1">
                      {koboDeployment.kobo_form_url}
                    </code>
                    <Button
                      icon="pi pi-external-link"
                      size="small"
                      text
                      onClick={() => window.open(koboDeployment.kobo_form_url, '_blank')}
                      tooltip="Open in KoboToolbox"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-medium text-sm text-600">Data Collection URL:</label>
                  <div className="flex align-items-center gap-2 mt-1">
                    <code className="text-sm bg-gray-100 p-2 border-round flex-1">
                      {koboDeployment.deployment_url}
                    </code>
                    <Button
                      icon="pi pi-copy"
                      size="small"
                      text
                      onClick={() => {
                        navigator.clipboard.writeText(koboDeployment.deployment_url);
                        toast.current?.show({
                          severity: 'success',
                          summary: 'Copied',
                          detail: 'Survey link copied to clipboard'
                        });
                      }}
                      tooltip="Copy survey link"
                    />
                  </div>
                </div>
              </div>
            </Panel>

            <div className="mt-3 p-3 bg-green-50 border-round">
              <div className="flex align-items-center gap-2 mb-2">
                <i className="pi pi-check-circle text-green-600" />
                <span className="font-medium text-green-900">Deployment Complete</span>
              </div>
              <div className="text-sm text-green-800">
                <p className="m-0 mb-1">• Form successfully created in KoboToolbox</p>
                <p className="m-0 mb-1">• Survey is live and ready for data collection</p>
                <p className="m-0">• You can now share the survey link with respondents</p>
              </div>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="text-center py-4">
            <i className="pi pi-times-circle text-4xl text-red-500 mb-3" />
            <p className="text-600 mb-3">Deployment to KoboToolbox failed</p>
            <div className="bg-red-50 border-round p-3 text-left">
              <div className="flex align-items-center gap-2 mb-2">
                <i className="pi pi-exclamation-triangle text-red-600" />
                <span className="font-medium text-red-900">Deployment Error</span>
              </div>
              <p className="text-sm text-red-800 m-0 mb-2">
                There was an error deploying the questionnaire to KoboToolbox. Please check your KoboToolbox account settings and try again.
              </p>
              <Button
                label="Retry Deployment"
                icon="pi pi-refresh"
                size="small"
                severity="danger"
                outlined
                onClick={handleDeployToKobo}
              />
            </div>
          </div>
        )}
      </Card>
    );
  };

  if (!activeWorkflow && !loading) {
    return (
      <Card>
        <div className="text-center py-6">
          <i className="pi pi-info-circle text-4xl text-400 mb-3" />
          <p className="text-600">No workflow data available. Start a workflow to see step-by-step results.</p>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-6">
          <i className="pi pi-spin pi-spinner text-4xl text-primary mb-3" />
          <p className="text-600">Loading workflow data...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="workflow-review-panel">
      <Toast ref={toast} />
      <ConfirmDialog />
      
      <div className="mb-4">
        <h2 className="text-2xl font-semibold mb-2">Workflow Review</h2>
        <p className="text-600">
          Review the output of each workflow step and approve the final deployment to KoboToolbox.
        </p>
      </div>

      <Accordion 
        activeIndex={activeIndex} 
        onTabChange={(e) => setActiveIndex(e.index as number)}
        className="workflow-accordion"
      >
        <AccordionTab header="Step 1: Indicator Extraction">
          {renderIndicatorsStep()}
        </AccordionTab>
        
        <AccordionTab header="Step 2: Questionnaire Design">
          {renderQuestionnaireStep()}
        </AccordionTab>
        
        <AccordionTab header="Step 3: KoboToolbox Deployment">
          {renderKoboDeploymentStep()}
        </AccordionTab>
      </Accordion>

      {/* Questionnaire Preview Dialog */}
      <Dialog
        header="Questionnaire Preview"
        visible={showQuestionnairePreview}
        style={{ width: '80vw', maxWidth: '800px' }}
        onHide={() => setShowQuestionnairePreview(false)}
        maximizable
      >
        <ScrollPanel style={{ width: '100%', height: '60vh' }}>
          <div className="p-3">
            <div className="mb-4">
              <h3 className="text-xl font-semibold mb-2">{questionnaire.title}</h3>
              <p className="text-600 mb-3">{questionnaire.description}</p>
              <Divider />
            </div>

            {questionnaire.sections.map((section) => (
              <div key={section.section_id} className="mb-4">
                <h4 className="text-lg font-semibold mb-3">{section.title}</h4>
                <p className="text-600 text-sm mb-4">{section.description}</p>

                <div className="flex flex-column gap-4">
                  {section.questions.map((question, questionIndex) => (
                    <div key={question.question_id} className="border-1 surface-border border-round p-3">
                      <div className="flex align-items-start gap-3">
                        <span className="bg-primary text-white border-circle w-2rem h-2rem flex align-items-center justify-content-center text-sm font-medium flex-shrink-0">
                          {questionIndex + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex align-items-start justify-content-between mb-2">
                            <p className="m-0 font-medium text-900">{question.text}</p>
                            {question.required && (
                              <Chip 
                                label="Required" 
                                className="text-xs ml-2"
                                style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}
                              />
                            )}
                          </div>
                          
                          <div className="flex align-items-center gap-2 mb-3">
                            <Chip 
                              label={question.type} 
                              className="text-xs"
                              style={{ backgroundColor: '#f3f4f6', color: '#374151' }}
                            />
                            <span className="text-xs text-500">ID: {question.question_id}</span>
                          </div>

                          {question.choices && (
                            <div>
                              <label className="text-sm font-medium text-600 mb-2 block">Answer Options:</label>
                              <div className="flex flex-wrap gap-2">
                                {question.choices.map((choice, choiceIndex) => (
                                  <div 
                                    key={choiceIndex}
                                    className="border-1 surface-border border-round px-2 py-1 text-sm bg-gray-50"
                                  >
                                    {choice}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollPanel>
      </Dialog>
    </div>
  );
};

export default WorkflowReviewPanel;